"use client";
import React, { useEffect, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Flex, Tag, theme, Tooltip } from '@basic-components';
import { DateTimeRangePicker } from '@general-components/DateTimeRangePicker';

const tagInputStyle: React.CSSProperties = {
  width: 64,
  height: 22,
  marginInlineEnd: 8,
  verticalAlign: 'top',
};

interface MultipleRangeTimePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  isView?: boolean;
}

export const MultipleRangeTimePicker: React.FC<MultipleRangeTimePickerProps> = (
  { value, onChange, placeholder, isView }
) => {
  const { token } = theme.useToken();
  const [tags, setTags] = useState<string[]>([]);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState<string | string[]>('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState<string | string[]>('');
  const okCalledRef = useRef(false);

  /** 将 "2024-01-01 10:00-2024-01-02 12:00" 拆分为 ["2024-01-01 10:00", "2024-01-02 12:00"] */
  const splitTagToArray = (tag: string): string[] => {
    const idx = tag.search(/-\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/);
    if (idx === -1) return [tag, ''];
    return [tag.slice(0, idx), tag.slice(idx + 1)];
  };

  useEffect(() => {
    if (value) {
      setTags(value.split(','));
    } else {
      setTags([]);
    }
  }, [value]);

  const handleClose = (removedTag: string) => {
    const newTags = tags.filter((tag) => tag !== removedTag);
    setTags(newTags);
  };

  const showInput = () => {
    setInputVisible(true);
  };

  const handleInputChange = (value: any) => {
    setInputValue(value);
  };

  const handleInputConfirm = (value: string | [string, string]) => {
    okCalledRef.current = true;
    const [start, end] = Array.isArray(value) ? value : [value, ''];
    // 两端时间都设置了才关闭
    if (start && end && start !== 'undefined' && end !== 'undefined') {
      const normalizedValue = `${ start }-${ end }`;
      if (!tags.includes(normalizedValue)) {
        const newTags = [...tags, normalizedValue];
        setTags(newTags);
        onChange?.(newTags.join(','));
      }
      setInputVisible(false);
      setInputValue('');
    }
  };

  const handleEditInputChange = (value: any) => {
    setEditInputValue(value);
  };

  const handleEditInputConfirm = (value: string | [string, string]) => {
    const [start, end] = Array.isArray(value) ? value : [value, ''];
    if (start && end && start !== 'undefined' && end !== 'undefined') {
      const newTag = `${ start }-${ end }`;
      const newTags = [...tags];
      newTags[editInputIndex] = newTag;
      setTags(newTags);
      onChange?.(newTags.join(','));
      setEditInputIndex(-1);
      setEditInputValue('');
    }
  };

  const tagPlusStyle: React.CSSProperties = {
    height: 22,
    background: token.colorBgContainer,
    borderStyle: 'dashed',
  };

  if (isView) return value || '--';

  return (
    <Flex gap="small" align="center" wrap>
      {tags.map<React.ReactNode>((tag, index) => {
        if (editInputIndex === index) {
          return (
            <DateTimeRangePicker
              key={tag}
              style={tagInputStyle}
              value={editInputValue}
              valueType="array"
              format="YYYY-MM-DD HH:mm"
              onChange={handleEditInputChange}
              onOk={handleEditInputConfirm}
              onOpenChange={(open) => {
                if (!open) {
                  setEditInputIndex(-1);
                  setEditInputValue('');
                }
              }}
            />
          );
        }
        const isLongTag = tag.length > 20;
        const tagElem = (
          <Tag
            key={tag}
            closable
            style={{ userSelect: 'none' }}
            onClose={() => handleClose(tag)}
          >
            <span
              onDoubleClick={(e) => {
                if (index !== 0) {
                  setEditInputIndex(index);
                  setEditInputValue(splitTagToArray(tag));
                  e.preventDefault();
                }
              }}
            >
              {isLongTag ? `${ tag.slice(0, 20) }...` : tag}
            </span>
          </Tag>
        );
        return isLongTag ? (
          <Tooltip title={tag} key={tag}>
            {tagElem}
          </Tooltip>
        ) : (
          tagElem
        );
      })}
      {inputVisible ? (
        <DateTimeRangePicker
          open={inputVisible}
          value={inputValue}
          format="YYYY-MM-DD HH:mm"
          valueType="array"
          onChange={handleInputChange}
          onOk={handleInputConfirm}
          onOpenChange={(open) => {
            if (!open) {
              if (okCalledRef.current) {
                // onOk 已调用但两端时间未全部设置，不关闭选择器
                okCalledRef.current = false;
              } else {
                // 点击外部区域关闭
                setInputVisible(false);
              }
            }
          }}
        />
      ) : (
        <Tag style={tagPlusStyle} icon={<PlusOutlined/>} onClick={showInput}>
          {placeholder || '添加标签'}
        </Tag>
      )}
    </Flex>
  );
};
